import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import type { Org, OrgMember, OrgInvite, User } from '../lib/types'

/** Family Circles — org layer: shared containers for trees, joined by link, governed by roles. */
export default function Circles({ user }: { user: User }) {
  const [orgs, setOrgs] = useState<Org[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [detail, setDetail] = useState<Awaited<ReturnType<typeof api.getOrg>> | null>(null)
  const [loading, setLoading] = useState(true)
  const [newName, setNewName] = useState('')
  const [inviteRole, setInviteRole] = useState<'member' | 'admin'>('member')
  const [inviteGate, setInviteGate] = useState(false)
  const [inviteUrl, setInviteUrl] = useState('')
  const [busy, setBusy] = useState(false)
  const [joinReqs, setJoinReqs] = useState<Awaited<ReturnType<typeof api.listJoinRequests>>['requests']>([])

  const load = () => api.listOrgs().then(o => {
    setOrgs(o)
    if (o.length && !selected) setSelected(o[0].id)
  }).finally(() => setLoading(false))
  useEffect(() => { load() }, []) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selected) {
      api.getOrg(selected).then(d => {
        setDetail(d)
        if (d.my_role === 'owner' || d.my_role === 'admin') {
          api.listJoinRequests(selected).then(r => setJoinReqs(r.requests)).catch(() => setJoinReqs([]))
        } else setJoinReqs([])
      }).catch(() => setDetail(null))
    }
    setInviteUrl('')
  }, [selected])

  const resolveReq = async (id: string, action: 'approve' | 'decline') => {
    await api.resolveJoinRequest(id, action)
    if (selected) {
      api.listJoinRequests(selected).then(r => setJoinReqs(r.requests)).catch(() => {})
      setDetail(await api.getOrg(selected))
    }
  }

  const create = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    try {
      const org = await api.createOrg(newName.trim())
      setNewName('')
      await load()
      setSelected(org.id)
    } finally { setBusy(false) }
  }

  const mintInvite = async () => {
    if (!selected) return
    const res = await api.createOrgInvite(selected, inviteRole, 0, 168, inviteGate)
    setInviteUrl(`${window.location.origin}${res.join_url}`)
    setDetail(await api.getOrg(selected))
  }

  const removeMember = async (m: OrgMember) => {
    if (!selected || !confirm(`Remove ${m.display} from this circle?`)) return
    await api.removeMember(selected, m.user_id)
    setDetail(await api.getOrg(selected))
  }

  const leave = async () => {
    if (!selected || !confirm('Leave this circle? You lose access to its trees unless directly shared.')) return
    await api.removeMember(selected, user.id)
    setSelected(null); setDetail(null)
    load()
  }

  const isAdmin = detail && (detail.my_role === 'owner' || detail.my_role === 'admin')

  if (loading) return <div className="flex justify-center py-32"><div className="animate-spin rounded-full h-9 w-9 border-b-2 border-gold-400" /></div>

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold text-slate-100">Family Circles</h1>
        <p className="text-sm text-slate-500 mt-1">Shared skies — trees inside a circle are visible to every member, governed by roles.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* circle list + create */}
        <div>
          <form onSubmit={create} className="flex gap-2 mb-4">
            <input required placeholder="New circle — e.g. The Ashfords" value={newName}
              onChange={e => setNewName(e.target.value)} className="input-dark !py-2 text-sm flex-1" />
            <button type="submit" disabled={busy || !newName.trim()} className="btn-gold !py-2 text-sm">＋</button>
          </form>
          <div className="space-y-2">
            {orgs.map(o => (
              <button key={o.id} onClick={() => setSelected(o.id)}
                className={`w-full text-left card-glass p-4 transition-colors ${selected === o.id ? '!border-gold-500/40' : 'hover:bg-white/[0.06]'}`}>
                <div className="flex items-center justify-between">
                  <p className="font-display font-semibold text-slate-100 truncate">{o.name}</p>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rootv-500/15 text-rootv-400 border border-rootv-500/25">{o.my_role}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">👥 {o.member_count} member{o.member_count === 1 ? '' : 's'} · 🌳 {o.tree_count} tree{o.tree_count === 1 ? '' : 's'}</p>
              </button>
            ))}
            {orgs.length === 0 && (
              <div className="card-glass p-8 text-center text-sm text-slate-500">
                <div className="text-4xl mb-3">🫂</div>
                No circles yet. Create one and invite the family with a single link.
              </div>
            )}
          </div>
        </div>

        {/* circle detail */}
        <div className="lg:col-span-2">
          {detail ? (
            <div className="card-glass p-6">
              <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
                <h2 className="font-display text-xl font-bold text-slate-100">{detail.org.name}</h2>
                <div className="flex gap-2">
                  {detail.my_role !== 'owner' && <button onClick={leave} className="btn-ghost !py-1.5 !px-3 text-xs">Leave circle</button>}
                  {detail.my_role === 'owner' && (
                    <button onClick={async () => { if (confirm('Dissolve this circle? Trees revert to personal.')) { await api.dissolveOrg(selected!); setSelected(null); setDetail(null); load() } }}
                      className="text-xs text-red-400/70 hover:text-red-400 px-2">Dissolve</button>
                  )}
                </div>
              </div>

              {/* invite link */}
              {isAdmin && (
                <div className="rounded-xl border border-gold-500/20 bg-gold-500/[0.05] p-4 mb-5">
                  <p className="text-sm text-slate-200 mb-2">🔗 Invite by link</p>
                  <div className="flex gap-2 flex-wrap items-center">
                    <select value={inviteRole} onChange={e => setInviteRole(e.target.value as 'member' | 'admin')} className="input-dark !py-2 text-sm w-32">
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                    </select>
                    <label className="flex items-center gap-1.5 text-xs text-slate-400 cursor-pointer">
                      <input type="checkbox" checked={inviteGate} onChange={e => setInviteGate(e.target.checked)} className="accent-amber-400" />
                      require approval
                    </label>
                    <button onClick={mintInvite} className="btn-gold !py-2 text-sm">Create join link</button>
                    {inviteUrl && (
                      <button onClick={() => navigator.clipboard?.writeText(inviteUrl)}
                        className="btn-ghost !py-2 text-xs font-mono truncate max-w-[260px]" title="Click to copy">
                        📋 {inviteUrl.replace(/^https?:\/\//, '')}
                      </button>
                    )}
                  </div>
                  {detail.invites.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {(detail.invites as OrgInvite[]).map(inv => (
                        <div key={inv.token} className="flex items-center justify-between text-xs text-slate-400 px-2 py-1 rounded bg-white/[0.03]">
                          <span className="font-mono">…/join/{inv.token}</span>
                          <span>{inv.role} · {inv.used_count}{inv.max_uses ? `/${inv.max_uses}` : ''} used · exp {inv.expires_at.slice(0, 10)}
                            <button onClick={async () => { await api.revokeOrgInvite(inv.token); setDetail(await api.getOrg(selected!)) }}
                              className="ml-2 text-red-400/70 hover:text-red-400">revoke</button>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* pending join requests */}
              {isAdmin && joinReqs.length > 0 && (
                <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.05] p-4 mb-5">
                  <p className="text-sm text-slate-200 mb-2">🚪 Join requests awaiting your blessing</p>
                  <div className="space-y-1.5">
                    {joinReqs.map(r => (
                      <div key={r.id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                        <div className="min-w-0">
                          <p className="text-sm text-slate-200 truncate">{r.user_name || r.email}</p>
                          <p className="text-[11px] text-slate-500">{r.email} · as {r.role} · asked {r.created_at.slice(0, 10)}</p>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <button onClick={() => resolveReq(r.id, 'approve')} className="text-xs px-2.5 py-1 rounded-lg border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10">Approve</button>
                          <button onClick={() => resolveReq(r.id, 'decline')} className="text-xs px-2.5 py-1 rounded-lg border border-red-500/25 text-red-400/90 hover:bg-red-500/10">Decline</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* members */}
              <p className="text-[10px] uppercase tracking-wider text-slate-600 mb-2">Members</p>
              <div className="space-y-1.5 mb-6">
                {(detail.members as OrgMember[]).map(m => (
                  <div key={m.user_id} className="flex items-center justify-between px-3 py-2 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                    <div className="min-w-0">
                      <p className="text-sm text-slate-200 truncate">{m.display}{m.user_id === user.id && <span className="text-slate-500"> (you)</span>}</p>
                      <p className="text-[11px] text-slate-500">{m.role} · joined {m.joined_at.slice(0, 10)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {detail.my_role === 'owner' && m.role !== 'owner' && (
                        <select value={m.role} onChange={async e => { await api.setMemberRole(selected!, m.user_id, e.target.value as 'admin' | 'member'); setDetail(await api.getOrg(selected!)) }}
                          className="input-dark !py-1 !px-2 text-xs w-24">
                          <option value="member">member</option>
                          <option value="admin">admin</option>
                        </select>
                      )}
                      {isAdmin && m.role !== 'owner' && m.user_id !== user.id && (
                        <button onClick={() => removeMember(m)} className="text-xs text-red-400/70 hover:text-red-400">Remove</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* trees in circle */}
              <p className="text-[10px] uppercase tracking-wider text-slate-600 mb-2">Trees in this circle</p>
              {detail.trees.length === 0 ? (
                <p className="text-sm text-slate-500">None yet — assign a tree from its ⚙ settings → Kin &amp; Access.</p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                  {detail.trees.map(t => (
                    <Link key={t.id} to={`/trees/${t.id}`} className="card-glass p-4 hover:bg-white/[0.06]">
                      <p className="font-display font-semibold text-slate-100 truncate">{t.name}</p>
                      <p className="text-xs text-slate-500 mt-1">✨ {t.person_count} souls · members join as {t.org_default_role}</p>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="card-glass p-12 text-center text-sm text-slate-500">Select or create a circle.</div>
          )}
        </div>
      </div>
    </div>
  )
}
